import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import dayjs from "dayjs";
import { EditIcon, PlusIcon, TrashIcon } from "lucide-react";
import { useState } from "react";
import {
  useClinicalCodeTransformations,
  type Transformation,
} from "@/clinical-codes-transformations/useClinicalCodeTransformations";

export function TransformationList({ onEdit }: { onEdit: (transformation: Transformation | null) => void }) {
  const { transformations, isLoading, deleteTransformation } = useClinicalCodeTransformations();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transformationToDelete, setTransformationToDelete] = useState<string | null>(null);

  const handleDeleteClick = (id: string) => {
    setTransformationToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (transformationToDelete) {
      await deleteTransformation.mutateAsync(transformationToDelete);
      setDeleteDialogOpen(false);
      setTransformationToDelete(null);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Existing clinical code transformations</CardTitle>
          </div>
          <Button onClick={() => onEdit(null)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            New Transformation
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading transformations...</div>
          ) : transformations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No transformations found. Create one to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Condition Sets</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transformations.map((transformation) => (
                  <TableRow key={transformation.id}>
                    <TableCell>
                      {transformation.code?.code} - {transformation.code?.description}
                    </TableCell>
                    <TableCell>{transformation.code_type}</TableCell>
                    <TableCell>{transformation.condition_sets.length}</TableCell>
                    <TableCell>{dayjs(transformation.created_at).tz().format("DD/MM/YY HH:mm")}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => onEdit(transformation)}>
                          <EditIcon className="h-4 w-4" />
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDeleteClick(transformation.id)}>
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Transformation</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this transformation? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleteTransformation.isPending}>
              {deleteTransformation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
